import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import Script from 'next/script';
import { useRef, useEffect } from 'react';

const GOOGLE_ADS_SCRIPT_PATH = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';
const GOOGLE_ADS_SIZE_ITEM_ID_HEIGHT = 1;
const GOOGLE_ADS_SLOT_TYPE_WITHIN_PAGE = 'withinPage';
const GOOGLE_ADS_SLOT_TYPE_OUT_OF_PAGE = 'outOfPage';

const createGetDivMinValue = size => (itemIndex) => {
    if (!size.length) {
        return undefined;
    }
    if (size === 'fluid' || size[0] === 'fluid') {
        return undefined;
    }
    if (typeof size[0] === 'number') {
        return size[itemIndex];
    }
    return Math.min(...size.map(item => item[itemIndex])
        .filter((item) => typeof item === 'number'));
};

const GoogleAds = ({ adUnitPath, className, divId, slot, targetingMap, onSlotRenderEndedRef, }) => {
    const containerRootElRef = useRef(null);
    const onAdUnitPathSlotRenderEndedRef = useRef(null);
    const getDivMinValue = createGetDivMinValue(slot.type === GOOGLE_ADS_SLOT_TYPE_WITHIN_PAGE
        ? slot.size
        : [0, 0]);
    useEffect(() => {
        const googleAdsContainer = document.getElementById(divId);
        if (!googleAdsContainer || googleAdsContainer.hasChildNodes()) {
            return undefined;
        }
        window.googletag = window.googletag ?? { cmd: [] };
        const { googletag } = window;
        let googleTagSlot = null;
        googletag.cmd.push(() => {
            googleTagSlot = slot.type === GOOGLE_ADS_SLOT_TYPE_WITHIN_PAGE
                ? googletag.defineSlot(adUnitPath, slot.size, divId)
                : googletag.defineOutOfPageSlot(adUnitPath, divId);
            if (!googleTagSlot) {
                return;
            }
            const publisherAds = googletag.pubads();
            const { current: onSlotRenderEnded } = onSlotRenderEndedRef ?? {};
            if (onSlotRenderEnded) {
                onAdUnitPathSlotRenderEndedRef.current = (event) => {
                    if (event.slot.getAdUnitPath() !== adUnitPath) {
                        return;
                    }
                    onSlotRenderEndedRef?.current?.(event);
                };
            }
            if (onAdUnitPathSlotRenderEndedRef.current) {
                publisherAds.addEventListener('slotRenderEnded', onAdUnitPathSlotRenderEndedRef.current);
            }
            googleTagSlot.addService(publisherAds);
            if (targetingMap) {
                const targetingEntires = Object.entries(targetingMap);
                for (const [targetKey, targetValue] of targetingEntires) {
                    googleTagSlot.setTargeting(targetKey, targetValue);
                }
            }
            publisherAds.enableSingleRequest();
            googletag.enableServices();
            googletag.display(divId);
        });
        return () => {
            googletag.cmd.push(() => {
                if (!googleTagSlot) {
                    return;
                }
                const publisherAds = googletag.pubads();
                if (onAdUnitPathSlotRenderEndedRef.current) {
                    publisherAds.removeEventListener('slotRenderEnded', onAdUnitPathSlotRenderEndedRef.current);
                }
                googletag.destroySlots([
                    googleTagSlot,
                ]);
            });
        };
    }, [
        adUnitPath,
        divId,
        slot,
        targetingMap,
        onSlotRenderEndedRef,
    ]);
    return (jsxs(Fragment, { children: [jsx(Script, { src: GOOGLE_ADS_SCRIPT_PATH, strategy: "afterInteractive" }), jsx("div", { ref: containerRootElRef, className: className, id: divId, style: {
                    minHeight: getDivMinValue(GOOGLE_ADS_SIZE_ITEM_ID_HEIGHT),
                } })] }));
};

export { GoogleAds as G, GOOGLE_ADS_SLOT_TYPE_OUT_OF_PAGE as a, GOOGLE_ADS_SLOT_TYPE_WITHIN_PAGE as b };
//# sourceMappingURL=component-FHXCpNwt.js.map
